// src/components/booking/ServiceStep.jsx
import { useState } from "react";
import { CheckBadge, SectionHeader, ServiceStepSkeleton, ServiceVisual } from "../ui";
import { useTenant } from "../../context/TenantContext";

export default function ServiceStep({ selected, onSelect }) {
  const { tenant, tenantLoading } = useTenant();
  const [activeCategory, setActiveCategory] = useState("All");
  const services = tenant?.services || [];

  if (tenantLoading) return <ServiceStepSkeleton />;

  // Build category list from the actual services — no hardcoding
  const categories = ["All", ...new Set(services.map(s => s.category).filter(Boolean))];
  const showFilter = categories.length > 2; // "All" + at least 2 distinct categories

  const filtered = activeCategory === "All"
    ? services
    : services.filter(s => s.category === activeCategory);

  return (
    <div>
      <SectionHeader
        title="Choose a Service"
        subtitle="Select the service you'd like to book today"
      />

      {/* Category filter — only shown when multiple categories exist */}
      {showFilter && <div className="flex gap-2 flex-wrap mb-6">
        {categories.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150
              ${activeCategory === cat
                ? "bg-brand-500 border-brand-500 text-white shadow-brand"
                : "border-surface-300 text-ink-500 hover:border-brand-300 hover:text-brand-600 bg-white"
              }`}>
            {cat}
          </button>
        ))}
      </div>}

      {services.length === 0 && (
        <div className="card p-12 text-center">
          <div className="text-4xl mb-3">✂️</div>
          <p className="text-ink-400 font-sans text-sm">No services available yet.</p>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(service => {
          const isSelected = selected?.id === service.id;
          return (
            <div key={service.id} onClick={() => onSelect(service)}
              className={`card-interactive p-5 relative group ${isSelected ? "card-selected" : ""}`}>

              {service.popular && !isSelected && (
                <div className="absolute top-3 right-3 badge-brand text-[10px] px-2 py-0.5">
                  Popular
                </div>
              )}
              {isSelected && (
                <div className="absolute top-3 right-3"><CheckBadge /></div>
              )}

              <ServiceVisual service={service} category={tenant?.category} className="mb-3" />
              <h3 className={`font-semibold text-lg mb-1 transition-colors
                ${isSelected ? "text-brand-700" : "text-ink-800 group-hover:text-brand-600"}`}>
                {service.name}
              </h3>
              <p className="text-ink-400 text-xs leading-relaxed mb-4 font-sans">
                {service.description}
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-surface-100">
                <span className="text-ink-400 text-xs font-mono">{service.duration} min</span>
                <span className={`font-semibold text-xl font-semibold
                  ${isSelected ? "text-brand-600" : "text-ink-700 group-hover:text-brand-500"} transition-colors`}>
                  ${service.price}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
